// 'app' is a native-app-only post: no external link, the full article lives
// in `articleHtml` and is read in-app instead of opening `url`.
export type NewsItemSourceType = 'wordpress' | 'manual' | 'generic' | 'app';
export type NewsItemStatus = 'draft' | 'visible' | 'archived';

/** Admin-facing News item (row + card merged by the backend). */
export interface NewsItemAdmin {
  id: string;
  title: string;
  publishDate: string | null; // YYYY-MM-DD; null on an undated app-post draft
  minutesToRead: number | null;
  summary: string;
  url: string | null; // null for an app-only post
  publisherName: string | null;
  publisherAvatarUrl: string | null;
  imageUrl: string | null;
  // Full article for an app-only (sourceType: 'app') post — null for every
  // website-linked post, which uses `summary` + `url` instead. Stored by the
  // backend as HTML restricted to the article editor's formatting (bold,
  // italic, lists, H2/H3, indentation).
  articleHtml: string | null;

  status: NewsItemStatus;
  sourceType: NewsItemSourceType;
  sourceUrl: string | null;
  sourceRef: string | null;
  createdByName: string | null;
  createdAt?: string;
}

/**
 * One row returned by the blog scan. Currently-live posts are filtered out
 * server-side; a `previouslyArchived` row is a post that was imported then
 * deleted — re-selecting it reinstates the row and re-imports from scratch.
 */
export interface DetectedNewsPost {
  sourceRef: string;
  canonicalUrl: string;
  title: string;
  publishDate: string;
  previouslyArchived?: boolean;
}

export interface NewsScanResult {
  sourceUrl: string;
  detected: DetectedNewsPost[];
}

export interface NewsImportResult {
  /** New draft rows created. */
  importedCount: number;
  /** Archived rows brought back as drafts and re-imported from scratch. */
  reinstatedCount: number;
  skippedCount: number;
  /** The draft rows this import created or reinstated. */
  items: NewsItemAdmin[];
}

/** Editable card fields shared by the add and edit forms. */
export interface NewsItemFormValues {
  title: string;
  summary: string;
  publishDate: string;
  minutesToRead: string; // kept as a string for the number input
  canonicalUrl: string;
  publisherName: string;
  publisherAvatarUrl: string;
  imageUrl: string;
}

export const EMPTY_NEWS_FORM: NewsItemFormValues = {
  title: '',
  summary: '',
  publishDate: new Date().toISOString().slice(0, 10),
  minutesToRead: '',
  canonicalUrl: '',
  publisherName: '',
  publisherAvatarUrl: '',
  imageUrl: '',
};

export function newsItemToForm(item: NewsItemAdmin): NewsItemFormValues {
  return {
    title: item.title ?? '',
    summary: item.summary ?? '',
    publishDate: (item.publishDate ?? '').slice(0, 10),
    minutesToRead: item.minutesToRead == null ? '' : String(item.minutesToRead),
    canonicalUrl: item.url ?? '',
    publisherName: item.publisherName ?? '',
    publisherAvatarUrl: item.publisherAvatarUrl ?? '',
    imageUrl: item.imageUrl ?? '',
  };
}

export function newsFormToPayload(values: NewsItemFormValues) {
  const minutes = parseInt(values.minutesToRead, 10);
  return {
    title: values.title.trim(),
    summary: values.summary.trim(),
    publishDate: values.publishDate,
    minutesToRead: Number.isFinite(minutes) && minutes > 0 ? minutes : null,
    canonicalUrl: values.canonicalUrl.trim(),
    publisherName: values.publisherName.trim() || null,
    publisherAvatarUrl: values.publisherAvatarUrl.trim() || null,
    imageUrl: values.imageUrl.trim() || null,
  };
}

/** Editable fields for a native-app-only post (no external link). */
export interface AppPostFormValues {
  title: string;
  publishDate: string;
  article: string;
  authorName: string;
  imageUrl: string;
}

export const EMPTY_APP_POST_FORM: AppPostFormValues = {
  title: '',
  publishDate: new Date().toISOString().slice(0, 10),
  article: '',
  authorName: '',
  imageUrl: '',
};

export function appPostToForm(item: NewsItemAdmin): AppPostFormValues {
  return {
    title: item.title ?? '',
    publishDate: (item.publishDate ?? '').slice(0, 10),
    article: item.articleHtml ?? '',
    authorName: item.publisherName ?? '',
    imageUrl: item.imageUrl ?? '',
  };
}

// Strips tags/entities down to plain text — used both to derive the
// auto-summary and to tell whether a rich-text value is "really" empty
// (Rich-text editors can leave markup like `<p><br></p>` behind with no visible text).
function stripHtml(html: string): string {
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function isArticleEmpty(articleHtml: string): boolean {
  return stripHtml(articleHtml) === '';
}

export function appPostFormToPayload(values: AppPostFormValues) {
  const articleText = stripHtml(values.article);
  return {
    title: values.title.trim(),
    publishDate: values.publishDate || null,
    // The backend sanitises this to the article allow-list, stores it and
    // derives the card's summary and reading time from it.
    articleHtml: articleText ? values.article : null,
    publisherName: values.authorName.trim() || null,
    imageUrl: values.imageUrl.trim() || null,
    sourceType: 'app',
    canonicalUrl: null,
  };
}
